import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { OdinService } from '../../core/services/odin.service';
import { RoomService, RoomUpdate } from '../../core/services/room.service';
import { Room } from '../../state/rooms.repository';
import { RoomDialog, RoomDialogData, RoomDialogResult } from './room-dialog/room-dialog';

/** Descriptor returned by {@link OdinService.createRoomOnly} (roomId + metadata). */
export type CreatedRoom = NonNullable<Awaited<ReturnType<OdinService['createRoomOnly']>>>;

const PANEL_OPTIONS = {
  panelClass: 'room-dialog',
  backdropClass: 'room-backdrop',
} as const;

/**
 * Opens the shared room dialog and runs the resulting create/update. Extracted
 * from room-login so every entry point — room-login, the preparation-area
 * empty state, and the rooms list in settings — offers the same flow without
 * duplicating the dialog + request + toast wiring. Callers decide what to do
 * with the room afterwards (room-login surfaces it in the search list; the prep
 * area retargets to it).
 */
@Injectable({ providedIn: 'root' })
export class RoomDialogService {
  private readonly _dialog = inject(MatDialog);
  private readonly _odin = inject(OdinService);
  private readonly _rooms = inject(RoomService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _translate = inject(TranslateService);

  /**
   * Prompts for room details, then opens the room. Returns the new room's
   * descriptor, or `null` if the user cancelled or creation failed (a failure
   * surfaces an error toast and leaves the dialog open to retry).
   *
   * The room is created while the dialog is still open, so it can confirm the
   * result in place — showing the invite link and the e2e password the creator
   * would otherwise never see again. This resolves once the user dismisses that
   * confirmation. Held in an object rather than a bare `let` so the closure's
   * write is visible to the return below.
   */
  async promptCreateRoom(): Promise<CreatedRoom | null> {
    const state: { created: CreatedRoom | null } = { created: null };

    await this._prompt({
      mode: 'create',
      create: async (result) => {
        state.created = await this._odin.createRoomOnly(result.roomName, {
          isPrivate: result.isPrivate,
          password: result.password,
          schedule: result.schedule ?? undefined,
        });
        if (!state.created) {
          this._toast(this._translate.instant('Could not create the room.'));
          return null;
        }
        return state.created.roomId;
      },
    });

    return state.created;
  }

  /**
   * Prompts for changes to an existing room, then applies them. Returns the
   * updated room, or `null` if the user cancelled or the update failed (a
   * failure surfaces an error toast).
   *
   * The room's encryption is fixed at creation, so the update never touches
   * `hasPassword` or the stored key — a moderator can edit a password room
   * whose key they do not hold.
   */
  async promptEditRoom(room: Room): Promise<Room | null> {
    const result = await this._prompt({ mode: 'edit', room });
    if (!result) return null;

    const patch: RoomUpdate = {
      name: result.roomName,
      isPrivate: result.isPrivate,
      schedule: result.schedule,
    };

    const updated = await this._rooms.updateRoom(room.roomId, patch);
    if (!updated) {
      this._toast(this._translate.instant('Could not update the room.'));
      return null;
    }
    return updated;
  }

  private async _prompt(data: RoomDialogData): Promise<RoomDialogResult | undefined> {
    const ref = this._dialog.open<RoomDialog, RoomDialogData, RoomDialogResult>(RoomDialog, {
      ...PANEL_OPTIONS,
      data,
    });
    return await firstValueFrom(ref.afterClosed());
  }

  private _toast(message: string): void {
    this._snackBar.open(message, undefined, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
